import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';
import {Hashtag} from "../../interface/hashtag";
import {CreateHashtagRequest} from "../../interface/createHashtagRequest";
import {HashtagService} from "../../service/hashtag.service";
import {MatDialog, MatDialogConfig, MatDialogRef} from "@angular/material/dialog";
import {noOnlySpacesValidator} from "../../validator/onlySpacesValidator";
import {HttpErrorResponse} from "@angular/common/http";
import {DeleteProfileCardComponent} from "../../profile-card/delete-profile-card/delete-profile-card.component";
import {DELETE_DESCRIPTION, DELETE_HASHTAG_TITLE} from "../../constants/deleteProfileCards.constant";
import {GetPageUtil} from "../get-page-util";

@Component({
  selector: 'app-hashtag-create-field',
  templateUrl: './hashtag-create-field.component.html',
  styleUrls: ['./hashtag-create-field.component.scss']
})
export class HashtagCreateFieldComponent implements OnInit {
  hashtagControl: FormControl | any;
  hashtagLength: number = 0;
  errorMessage: string | null = null;
  hashtags: Hashtag[] = [];
  currentPage: number = 0;
  pageSize: number = 12;
  currentHashtagSize: number | any;
  dialogRef: MatDialogRef<DeleteProfileCardComponent> | undefined;
  moreHashtagsExists: boolean = false;
  totalHashtagSize: number = 0;
  isLoading: boolean = false;
  private readonly getPageUtil = new GetPageUtil();

  public readonly HASH_TAG_ERROR_SIZE_MESSAGE = 'Tag should be between 2 to 25 characters long and contain only letters'

  constructor(
    private hashtagService: HashtagService,
    private matDialog: MatDialog,
    private changeDetectorRef: ChangeDetectorRef
  ) {
  }

  ngOnInit() {
    this.hashtagControl = new FormControl('', [
      Validators.required,
      Validators.pattern(/^[\p{L}\s]+$/u),
      Validators.minLength(2),
      Validators.maxLength(25),
      noOnlySpacesValidator()
    ]);
    this.clearErrorMessageOnChange()
    this.getTotalHashtagCount();
    this.getPagedHashtags();
  }

  getPagedHashtags(): void {
    this.hashtagService.search('', this.currentPage, this.pageSize).subscribe({
      next: (hashtags: Array<Hashtag>) => {
        this.currentHashtagSize = hashtags.length;
        this.hashtags = hashtags;
        this.openButtonsForPagination();
      },
      error: (error: HttpErrorResponse) => {
        console.error('Failed to load hashtags', error);
      }
    });
  }

  openButtonsForPagination(): void {
    this.moreHashtagsExists = this.totalHashtagSize > this.pageSize;
  }

  getTotalHashtagCount(): void {
    this.hashtagService.getTotalHashtagCount().subscribe(
      count =>{
        this.totalHashtagSize = count;
      }
    )
  }

  getMoreHashtags(): void {
    this.hashtagService.search('', this.currentPage, this.pageSize).subscribe({
      next: (response: Array<Hashtag>) => {
        if (response.length > 0) {
          this.hashtags = response;
          this.currentHashtagSize += response.length;
          this.isLoading = false;
          this.openButtonsForPagination();
        } else {
          this.isLoading = false;
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
      }
    });
  }

  addHashtag(newHashtag: Hashtag): void {
    this.hashtags.unshift(newHashtag);
  }

  generateTag() {
    if (this.hashtagControl.valid) {
      const newHashtag: CreateHashtagRequest = {
        name: this.hashtagControl.value
      };
      this.hashtagService.createHashtag(newHashtag).subscribe({
        next: (hashtag: Hashtag) => {
          this.addHashtag(hashtag);
          this.hashtagControl.reset();
          this.hashtagLength = 0;
          this.errorMessage = null;
          this.currentHashtagSize++;
          this.totalHashtagSize++;
          this.openButtonsForPagination();
          this.hashtagControl.setValue('');
          this.hashtagControl.markAsUntouched();
          this.hashtagControl.markAsPristine();
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 409) {
            this.errorMessage = 'Hashtag name already exists. Please try a different name';
          }
        }
      });
    }
  }

  deleteHashtag(hashtag: Hashtag) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      hashtagData: hashtag
    }
    this.dialogRef = this.matDialog.open(DeleteProfileCardComponent, {
      data: {
        deleteTitle: DELETE_HASHTAG_TITLE,
        deleteDescription: DELETE_DESCRIPTION,
      }
    });
    this.dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.hashtagService.deleteHashtagById(hashtag.id).subscribe(() => {
          this.hashtags = this.hashtags.filter(h => h.name !== hashtag.name);
          this.changeDetectorRef.detectChanges();
          this.currentHashtagSize--;
          this.totalHashtagSize--;
          this.openButtonsForPagination();
        })
      }
    })
  }

  resetForm() {
    this.hashtagControl.setValue('');
    this.hashtagControl.markAsUntouched();
    this.hashtagControl.markAsPristine();
    this.hashtagLength = 0;
    this.errorMessage = null;
  }

  goToPage(page: number) {
    if (page >= 0 && page < this.totalHashtagSize) {
      this.currentPage = page;
      this.getMoreHashtags();
    }
  }

  getPageNumbers(): number[] {
    return this.getPageUtil.getPageNumbers(this.currentPage, this.totalHashtagSize, this.pageSize);
  }

  private clearErrorMessageOnChange(): void {
    this.hashtagControl.valueChanges.subscribe({
      next: () => {
        this.errorMessage = null;
      }
    });
  }

  protected readonly Math = Math;
}
